import {
  LISTING_PRICE_OPTIONS,
  LISTING_PRICE_SETTINGS_SECTION,
  PRICE_LISTING_TEXT,
  SHOW_ON_LISTING_LABEL,
  applyListingDefaultsToOpenLots,
  countLotsOffListingDefaults,
  listingForPrice,
  parseListingPriceSettings,
  saveListingPriceSettings,
  type ListingPriceSettings,
  type PriceKey,
} from '@auctisync/api-client';
import { Badge, Button, LearnMoreLink } from '@auctisync/ui';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HouseSelector } from '../../components/HouseSelector';
import { useToast } from '../../components/Toast';
import { useOwnedTenants } from '../../hooks/useOwnedTenants';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

/**
 * Tenant admin — AI Settings.
 *
 * Lets each tenant toggle which inventory-item fields the "AI Complete"
 * button will draft. The Edge Function always runs Claude on every
 * field (simpler prompt + better joint reasoning); the UI honors these
 * flags when deciding which suggested values to show + default-check in
 * the review modal.
 *
 * Fields are grouped (Identity / Specs / Pricing) for readability. The
 * tenants.ai_settings column stores the persisted state.
 *
 * Saves go through the owner-only set_ai_settings RPC — NOT a direct
 * tenants.update(). Owners hold no RLS UPDATE grant on public.tenants, so the
 * old direct update silently matched 0 rows and toasted "Saved." while
 * persisting nothing (task d590dea5). Staff can still VIEW the settings (the
 * tenants SELECT is RLS-granted to tenant members); saving requires the owner.
 * Multi-house owners pick the house via HouseSelector (review finding #9).
 *
 * The Pricing box also holds which prices a NEW lot shows bidders ("Show on
 * listing", `tenants.listing_price_settings`). Jim, 2026-10-06, ticked every
 * price here and found his lots still showed only the MSRP — those defaults
 * then lived on a separate Listing Prices page. Now one table, laid out like
 * the lot's "Prices to review": Price | AI fills in | Show on listing. The
 * listing column works with AI Complete off, and saves through the owner-only
 * `set_listing_price_settings`. When it changes, the page offers to give the
 * house's lots that haven't closed the same choices (Jim: yes) — counted
 * first, applied only on a click, both by `apply_listing_price_defaults`.
 */

type TenantRow = {
  id: string;
  name: string;
  slug: string;
  ai_settings: {
    enabled?: boolean;
    fields?: Record<string, boolean>;
  } | null;
  listing_price_settings: unknown;
};

const DEFAULT_FIELDS: Record<string, boolean> = {
  title: true,
  description: true,
  condition: true,
  category: true,
  mpn: true,
  upc: true,
  brand: true,
  model: true,
  year: true,
  material: true,
  color: true,
  dimensions: true,
  msrp: true,
  retail_price: true,
  starting_bid: true,
  reserve: true,
  estimate_min: true,
  estimate_max: true,
};

/**
 * Typed bridge for the set_ai_settings RPC. `database.ts` is regenerated
 * centrally after this migration is applied; until then the name isn't in the
 * generated union, so we call through a narrow, fully-typed signature (no
 * `any`) rather than widening the shared client. Remove once db:types runs.
 */
type AiSettingsRpc = (
  fn: 'set_ai_settings',
  args: { p_enabled: boolean; p_fields: Record<string, boolean>; p_tenant_id: string | null },
) => Promise<{ data: unknown; error: { message: string } | null }>;
const aiSettingsRpc = supabase.rpc.bind(supabase) as unknown as AiSettingsRpc;

type FieldMeta = { key: string; label: string; hint?: string };
type PricingFieldMeta = FieldMeta & { price: PriceKey };

const IDENTITY_FIELDS: FieldMeta[] = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'condition', label: 'Condition', hint: 'new / like-new / used — …' },
  { key: 'category', label: 'Category', hint: 'matched to your existing taxonomy' },
  { key: 'mpn', label: 'MPN', hint: 'manufacturer part number' },
  { key: 'upc', label: 'UPC', hint: 'barcode digits' },
];

const SPEC_FIELDS: FieldMeta[] = [
  { key: 'brand', label: 'Brand' },
  { key: 'model', label: 'Model' },
  { key: 'year', label: 'Year of manufacture' },
  { key: 'material', label: 'Material' },
  { key: 'color', label: 'Color' },
  { key: 'dimensions', label: 'Dimensions' },
];

const PRICING_FIELDS: PricingFieldMeta[] = [
  { key: 'msrp', price: 'msrp_cents', label: 'MSRP', hint: 'from web research' },
  {
    key: 'retail_price',
    price: 'retail_price_cents',
    label: 'Retail price',
    hint: 'typically 40–75% of MSRP',
  },
  {
    key: 'starting_bid',
    price: 'starting_bid_cents',
    label: 'Starting bid',
    hint: 'typically 10–20% of MSRP',
  },
  { key: 'reserve', price: 'reserve_cents', label: 'Reserve', hint: 'typically 40–60% of MSRP' },
  {
    key: 'estimate_min',
    price: 'estimate_min_cents',
    label: 'Estimate (low)',
    hint: 'typically 35–55% of MSRP',
  },
  {
    key: 'estimate_max',
    price: 'estimate_max_cents',
    label: 'Estimate (high)',
    hint: 'typically 55–80% of MSRP',
  },
];

/** "Also change the 10 lots that haven't closed?" — Jim's words, 2026-10-06. */
function openLotsPhrase(n: number): string {
  return n === 1 ? 'the 1 lot that hasn’t closed' : `the ${n} lots that haven’t closed`;
}

const sameListing = (a: ListingPriceSettings, b: ListingPriceSettings) =>
  LISTING_PRICE_OPTIONS.every((o) => a[o.key] === b[o.key]);

export function AdminAiSettings() {
  const { userId } = useSession();
  const toast = useToast();
  const owned = useOwnedTenants();
  const [tenant, setTenant] = useState<TenantRow | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [fields, setFields] = useState<Record<string, boolean>>(DEFAULT_FIELDS);
  // Which prices a new lot shows bidders — as on screen, and as last saved.
  const [listing, setListing] = useState<ListingPriceSettings>(() =>
    parseListingPriceSettings(null),
  );
  const [savedListing, setSavedListing] = useState<ListingPriceSettings>(() =>
    parseListingPriceSettings(null),
  );
  // Lots that haven't closed and show different prices from the saved
  // defaults — set after a save changes them, so the page can offer to change
  // those lots too. null = nothing to ask.
  const [offDefaults, setOffDefaults] = useState<number | null>(null);
  const [applying, setApplying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { hash } = useLocation();

  const load = useCallback(async () => {
    if (userId === null) return;
    setLoading(true);
    setError(null);
    setOffDefaults(null);
    // Owners read the SELECTED house; staff (who own nothing) fall back to
    // their staff tenant for a read-only view — saving is owner-gated by the
    // set_ai_settings RPC.
    let tenantId = owned.selectedId;
    if (!tenantId) {
      const { data: roleRows } = await supabase
        .from('user_roles')
        .select('tenant_id, role')
        .eq('user_id', userId)
        .in('role', ['tenant_owner', 'tenant_staff']);
      tenantId = (roleRows ?? []).find((r) => r.tenant_id)?.tenant_id ?? null;
    }
    if (!tenantId) {
      setError('AI settings are available to tenant owners and staff.');
      setLoading(false);
      return;
    }
    const { data: t, error: tErr } = await supabase
      .from('tenants')
      .select('id, name, slug, ai_settings, listing_price_settings')
      .eq('id', tenantId)
      .maybeSingle();
    if (tErr) {
      setError(tErr.message);
      setLoading(false);
      return;
    }
    setTenant((t ?? null) as TenantRow | null);
    const raw = (t?.ai_settings ?? {}) as { enabled?: boolean; fields?: Record<string, boolean> };
    setEnabled(raw.enabled ?? true);
    setFields({ ...DEFAULT_FIELDS, ...(raw.fields ?? {}) });
    const shown = parseListingPriceSettings(t?.listing_price_settings);
    setListing(shown);
    setSavedListing(shown);
    setLoading(false);
  }, [userId, owned.selectedId]);

  useEffect(() => {
    // Wait for the owned-tenants lookup so multi-house owners load the
    // SELECTED house on first render rather than an arbitrary role row.
    if (owned.loading) return;
    void load();
  }, [load, owned.loading]);

  // The lot page's "Change which prices show by default →" lands here, on the
  // Pricing box. The box only exists once the settings have loaded.
  useEffect(() => {
    if (loading || hash !== `#${LISTING_PRICE_SETTINGS_SECTION}`) return;
    document.getElementById(LISTING_PRICE_SETTINGS_SECTION)?.scrollIntoView({ block: 'start' });
  }, [loading, hash]);

  async function save() {
    if (!tenant) return;
    setSaving(true);
    setError(null);
    setOffDefaults(null);
    // Owner-only SECURITY DEFINER RPC — a direct tenants.update() matches 0
    // rows under RLS and reports success without persisting anything.
    const { error: upErr } = await aiSettingsRpc('set_ai_settings', {
      p_enabled: enabled,
      p_fields: fields,
      p_tenant_id: tenant.id,
    });
    if (upErr) {
      setSaving(false);
      setError(ownerOnly(upErr.message) ? OWNER_ONLY : upErr.message);
      toast.error('AI settings could not be saved.');
      return;
    }
    if (sameListing(listing, savedListing)) {
      setSaving(false);
      toast.success('Saved. AI Complete will honor these choices on the next run.');
      return;
    }
    try {
      const saved = await saveListingPriceSettings(supabase, listing, tenant.id);
      setListing(saved);
      setSavedListing(saved);
    } catch (e) {
      setSaving(false);
      const message = e instanceof Error ? e.message : String(e);
      setError(ownerOnly(message) ? OWNER_ONLY : message);
      toast.error('Which prices show could not be saved.');
      return;
    }
    toast.success('Saved. New lots will show the prices you ticked.');
    // The defaults changed: ask about the lots that already exist, with the
    // number that would really change.
    try {
      const n = await countLotsOffListingDefaults(supabase, tenant.id);
      if (n > 0) setOffDefaults(n);
    } catch (e) {
      setError(
        `Saved, but the lots that haven’t closed could not be checked: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    }
    setSaving(false);
  }

  async function applyToOpenLots() {
    if (!tenant) return;
    setApplying(true);
    setError(null);
    try {
      const n = await applyListingDefaultsToOpenLots(supabase, tenant.id);
      setOffDefaults(null);
      toast.success(n === 1 ? 'Changed 1 lot.' : `Changed ${n} lots.`);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(ownerOnly(message) ? OWNER_ONLY : message);
      toast.error('The lots could not be changed.');
    }
    setApplying(false);
  }

  function toggleField(key: string, next: boolean) {
    setFields((prev) => ({ ...prev, [key]: next }));
  }

  function toggleGroup(keys: string[], next: boolean) {
    setFields((prev) => {
      const patch: Record<string, boolean> = {};
      for (const k of keys) patch[k] = next;
      return { ...prev, ...patch };
    });
  }

  if (loading) return <div style={wrapStyle}>Loading AI settings…</div>;

  return (
    <div style={wrapStyle}>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          AI Settings
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          Choose which inventory fields the <strong>AI Complete</strong> button drafts for{' '}
          {tenant?.name ?? 'your auction house'}. Fields you uncheck are hidden in the review modal
          and never overwritten by AI output.{' '}
          <LearnMoreLink helpSlug="ai-complete-settings" newTab>
            How AI Complete works
          </LearnMoreLink>
        </p>
      </header>

      <HouseSelector
        tenants={owned.tenants}
        selectedId={owned.selectedId}
        onSelect={owned.setSelectedId}
        disabled={saving}
      />

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      <section style={cardStyle}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            fontSize: '0.95rem',
            fontWeight: 500,
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
            }}
          />
          <span>
            Enable AI Complete for {tenant?.name ?? 'this tenant'}
            {enabled ? null : (
              <>
                {' '}
                <Badge variant="outline">Disabled</Badge>
              </>
            )}
          </span>
        </label>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0.5rem 0 0' }}>
          When disabled, the AI Complete button won&apos;t appear on inventory items. The Draft and
          Research buttons stay available regardless.
        </p>
      </section>

      <FieldGroup
        title="Identity"
        description="Core product identification — what it is and how to describe it."
        fields={IDENTITY_FIELDS}
        values={fields}
        onToggle={toggleField}
        onAll={(v) =>
          toggleGroup(
            IDENTITY_FIELDS.map((f) => f.key),
            v,
          )
        }
        disabled={!enabled}
      />
      <FieldGroup
        title="Specs"
        description="Structured attributes stored in the item's custom_fields."
        fields={SPEC_FIELDS}
        values={fields}
        onToggle={toggleField}
        onAll={(v) =>
          toggleGroup(
            SPEC_FIELDS.map((f) => f.key),
            v,
          )
        }
        disabled={!enabled}
      />
      <PricingGroup
        fields={fields}
        onToggle={toggleField}
        onAll={(v) =>
          toggleGroup(
            PRICING_FIELDS.map((f) => f.key),
            v,
          )
        }
        aiDisabled={!enabled}
        listing={listing}
        onListing={(patch) => {
          setListing((prev) => ({ ...prev, ...patch }));
          // The question was about what was just saved; a new change asks again
          // on its own Save.
          setOffDefaults(null);
        }}
        listingDisabled={saving}
      >
        {offDefaults !== null ? (
          <div role="status" data-testid="listing-open-lots" style={askStyle}>
            <p style={{ margin: 0, color: 'var(--text)' }}>
              <strong>Also change {openLotsPhrase(offDefaults)}?</strong> They show different prices
              from the ones you just saved. Lots that have closed keep theirs.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
              <Button
                type="button"
                data-testid="listing-open-lots-change"
                disabled={applying}
                onClick={() => {
                  void applyToOpenLots();
                }}
              >
                {applying ? 'Changing…' : 'Change them'}
              </Button>
              <Button
                type="button"
                variant="outline"
                data-testid="listing-open-lots-leave"
                disabled={applying}
                onClick={() => {
                  setOffDefaults(null);
                }}
              >
                Leave them
              </Button>
            </div>
          </div>
        ) : null}
      </PricingGroup>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <Button
          onClick={() => {
            void save();
          }}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => {
            void load();
          }}
        >
          Revert
        </Button>
      </div>
    </div>
  );
}

const OWNER_ONLY = 'Only the tenant owner can change AI settings.';
const ownerOnly = (message: string) => message.includes('42501') || /owner/i.test(message);

/**
 * The Pricing box: per price, whether AI research fills it in and whether a
 * new lot shows it to bidders. Which prices get a box — and that the starting
 * bid always shows and the reserve never does — is `listingForPrice`, the rule
 * the lot page and the Cataloging app use. Both ends of the estimate share
 * one choice, as on the lot.
 */
function PricingGroup({
  fields,
  onToggle,
  onAll,
  aiDisabled,
  listing,
  onListing,
  listingDisabled,
  children,
}: {
  fields: Record<string, boolean>;
  onToggle: (key: string, next: boolean) => void;
  onAll: (v: boolean) => void;
  aiDisabled: boolean;
  listing: ListingPriceSettings;
  onListing: (patch: Partial<ListingPriceSettings>) => void;
  listingDisabled: boolean;
  children?: React.ReactNode;
}) {
  return (
    <section
      id={LISTING_PRICE_SETTINGS_SECTION}
      data-testid="pricing-settings"
      style={{ ...cardStyle, scrollMarginTop: '1rem' }}
    >
      <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
        Pricing
      </h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.15rem 0 0.5rem' }}>
        <strong>AI fills in</strong> — the prices AI research suggests, each worked out from the
        MSRP it finds. <strong>{SHOW_ON_LISTING_LABEL}</strong> — the prices a new lot shows
        bidders. Each lot can change its own.{' '}
        <LearnMoreLink helpSlug="listing-prices" newTab>
          Which prices bidders see
        </LearnMoreLink>
      </p>
      <table style={{ width: '100%', fontSize: '0.9rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--text-muted)', verticalAlign: 'top' }}>
            <th style={cellStyle}>Price</th>
            <th style={cellStyle}>
              <div>AI fills in</div>
              <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.2rem' }}>
                <button
                  type="button"
                  onClick={() => onAll(true)}
                  disabled={aiDisabled}
                  style={miniButton}
                  aria-label="AI fills in every price"
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => onAll(false)}
                  disabled={aiDisabled}
                  style={miniButton}
                  aria-label="AI fills in no prices"
                >
                  None
                </button>
              </div>
            </th>
            <th style={cellStyle}>{SHOW_ON_LISTING_LABEL}</th>
          </tr>
        </thead>
        <tbody>
          {PRICING_FIELDS.map((f) => {
            const choice = listingForPrice(f.price);
            return (
              <tr key={f.key} data-testid={`pricing-row-${f.key}`}>
                <td style={cellStyle}>
                  <div style={{ color: 'var(--text)' }}>{f.label}</div>
                  {f.hint && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{f.hint}</div>
                  )}
                </td>
                <td style={{ ...cellStyle, opacity: aiDisabled ? 0.55 : 1 }}>
                  <input
                    type="checkbox"
                    aria-label={`AI fills in ${f.label}`}
                    data-testid={`ai-fills-${f.key}`}
                    checked={Boolean(fields[f.key])}
                    onChange={(e) => onToggle(f.key, e.target.checked)}
                    disabled={aiDisabled}
                  />
                </td>
                <td style={cellStyle}>
                  {choice === 'always' || choice === 'never' ? (
                    <span style={{ color: 'var(--text-muted)' }}>{PRICE_LISTING_TEXT[choice]}</span>
                  ) : (
                    <input
                      type="checkbox"
                      aria-label={`Show ${f.label} on new lots' listings`}
                      data-testid={`listing-default-${f.key}`}
                      checked={listing[choice]}
                      onChange={(e) => {
                        onListing({ [choice]: e.target.checked });
                      }}
                      disabled={listingDisabled}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {children}
    </section>
  );
}

function FieldGroup({
  title,
  description,
  fields,
  values,
  onToggle,
  onAll,
  disabled,
}: {
  title: string;
  description: string;
  fields: FieldMeta[];
  values: Record<string, boolean>;
  onToggle: (key: string, next: boolean) => void;
  onAll: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <section style={{ ...cardStyle, opacity: disabled ? 0.55 : 1 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '0.5rem',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
            {title}
          </h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.15rem 0 0' }}>
            {description}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button type="button" onClick={() => onAll(true)} disabled={disabled} style={miniButton}>
            Select all
          </button>
          <button type="button" onClick={() => onAll(false)} disabled={disabled} style={miniButton}>
            Clear
          </button>
        </div>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.35rem' }}>
        {fields.map((f) => (
          <li key={f.key}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                fontSize: '0.9rem',
                padding: '0.3rem 0.4rem',
                borderRadius: '0.25rem',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={Boolean(values[f.key])}
                onChange={(e) => onToggle(f.key, e.target.checked)}
                disabled={disabled}
              />
              <span style={{ color: 'var(--text)' }}>{f.label}</span>
              {f.hint && (
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>— {f.hint}</span>
              )}
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

const wrapStyle: React.CSSProperties = {
  padding: '2rem 2.25rem',
  maxWidth: '56rem',
};

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
  marginBottom: '1rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
};

const cellStyle: React.CSSProperties = {
  padding: '0.35rem 0.75rem 0.35rem 0',
  verticalAlign: 'top',
};

const askStyle: React.CSSProperties = {
  marginTop: '0.85rem',
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  fontSize: '0.9rem',
};

const miniButton: React.CSSProperties = {
  fontSize: '0.75rem',
  padding: '0.25rem 0.55rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  borderRadius: '0.3rem',
  cursor: 'pointer',
};
